import { useEffect, useState } from 'react';
import { apiFetch } from '../api/client';
import { authHeaders } from '../api/dev';

type Props = {
  svc: 'forecast-push' | 'lightning-push';
  webhookUrl: string;
  setWebhookUrl: (s: string) => void;
  apiKeyId: string;
  setApiKeyId: (s: string) => void;
  hasSecret: boolean;
  setHasSecret: (b: boolean) => void;
  onSaveConfig: () => Promise<void>;
};

export default function WebhookConfigSection({ svc, webhookUrl, setWebhookUrl, apiKeyId, setApiKeyId, hasSecret, setHasSecret, onSaveConfig }: Props) {
  const [devKeys, setDevKeys] = useState<Array<{ keyId: string; status: string }>>([]);
  const [generatedSecret, setGeneratedSecret] = useState<string>('');
  const [hasApiKey, setHasApiKey] = useState<boolean>(Boolean(apiKeyId));
  const [revealedKey, setRevealedKey] = useState<string>('');
  const [showKey, setShowKey] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [secretCopied, setSecretCopied] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setErr] = useState<string>('');
  const [ok, setOk] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch('/dev/keys', { headers: { ...authHeaders() } });
        const j = await r.json().catch(() => ({}));
        if (r.ok && Array.isArray(j.items)) setDevKeys(j.items);
      } catch {}
    })();
  }, []);

  return (
    <div className="card" style={{ maxWidth: 900, padding: 16, marginBottom: 12 }}>
      <h3 style={{ marginTop: 0 }}>Webhook Configuration</h3>
      <div className="form-grid">
        {/* Webhook URL */}
        <div className="form-row">
          <div className="form-label">Webhook URL</div>
          <div className="form-value">
            <input className="txt" style={{ flex: 1, minWidth: 260 }} value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} placeholder="https://example.com/webhook" />
          </div>
        </div>
        {/* Signing Secret */}
        <div className="form-row">
          <div className="form-label">Signing Secret</div>
          <div className="form-value">
            {hasSecret ? (
              <>
                <span className="muted">Secret is set.</span>
                <button className="btn info" onClick={async () => {
                  try {
                    setGeneratedSecret('');
                    const r = await apiFetch(`/dev/${svc}/secret`, { method: 'POST', headers: { ...authHeaders() } });
                    const j = await r.json().catch(() => ({}));
                    if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
                    setHasSecret(true);
                    setGeneratedSecret(j.secret || '');
                  } catch (e: any) { setErr(e?.message || String(e)); }
                }}>Regenerate Secret</button>
              </>
            ) : (
              <>
                <span className="muted">No secret set. Generate one to sign webhooks.</span>
                <button className="btn info" onClick={async () => {
                  try {
                    const r = await apiFetch(`/dev/${svc}/secret`, { method: 'POST', headers: { ...authHeaders() } });
                    const j = await r.json().catch(() => ({}));
                    if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
                    setHasSecret(true);
                    setGeneratedSecret(j.secret || '');
                  } catch (e: any) { setErr(e?.message || String(e)); }
                }}>Generate Secret</button>
              </>
            )}
            {generatedSecret && (
              <div className="card" style={{ padding: 10, marginTop: 8 }}>
                <div className="muted" style={{ marginBottom: 4 }}>Copy this secret now; it will be shown only once.</div>
                <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
                  <code style={{ userSelect: 'all', overflowX: 'auto' }}>{generatedSecret}</code>
                  <button
                    className="btn secondary"
                    title="Copy"
                    aria-label="Copy secret"
                    onClick={async () => {
                      try {
                        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(generatedSecret);
                        else {
                          const ta = document.createElement('textarea');
                          ta.value = generatedSecret; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
                        }
                        setSecretCopied(true); setTimeout(() => setSecretCopied(false), 1500);
                      } catch {}
                    }}
                    style={{ display: 'grid', placeItems: 'center', padding: '6px 10px' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                  {secretCopied && <span className="muted" style={{ color: 'lightgreen' }}>Copied!</span>}
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Forecast API Key */}
        <div className="form-row">
          <div className="form-label">Forecast API Key</div>
          <div className="form-value">
            {hasApiKey ? (
              <>
                <span className="muted">Using key: <code>{apiKeyId}</code></span>
                <button className="btn info" onClick={async () => {
                  try {
                    setShowKey(true);
                    const r = await apiFetch(`/dev/keys/${encodeURIComponent(apiKeyId)}`, { headers: { ...authHeaders() } });
                    const j = await r.json().catch(() => ({}));
                    if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
                    setRevealedKey(j.key || '');
                  } catch (e: any) { setErr(e?.message || String(e)); }
                }}>Reveal</button>
                <button className="btn neutral" onClick={() => { setHasApiKey(false); setShowKey(false); setRevealedKey(''); }}>Change…</button>
              </>
            ) : (
              <>
                <select className="txt" style={{ minWidth: 240 }} value={apiKeyId} onChange={e => { setApiKeyId(e.target.value); setRevealedKey(''); setShowKey(false); }}>
                  <option value="">Select a key…</option>
                  {devKeys.map(k => (
                    <option key={k.keyId} value={k.keyId}>{k.keyId} ({k.status})</option>
                  ))}
                </select>
                <button className="btn info" disabled={!apiKeyId} onClick={async () => {
                  try {
                    setShowKey(true);
                    const r = await apiFetch(`/dev/keys/${encodeURIComponent(apiKeyId)}`, { headers: { ...authHeaders() } });
                    const j = await r.json().catch(() => ({}));
                    if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
                    setRevealedKey(j.key || '');
                  } catch (e: any) { setErr(e?.message || String(e)); }
                }}>Reveal Value</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Generated secret card now appears inline under Signing Secret row */}

      {showKey && revealedKey && (
        <div className="card" style={{ padding: 10, marginTop: 8, marginBottom: 8 }}>
          <div className="muted" style={{ marginBottom: 4 }}>Key value (hide when done)</div>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <code style={{ userSelect: 'all' }}>{revealedKey}</code>
            <button
              className="btn secondary"
              title="Copy"
              aria-label="Copy API key"
              onClick={async () => {
                try {
                  if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(revealedKey);
                  else {
                    const ta = document.createElement('textarea');
                    ta.value = revealedKey; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
                  }
                  setCopied(true); setTimeout(() => setCopied(false), 1500);
                } catch {}
              }}
              style={{ display: 'grid', placeItems: 'center', padding: '6px 10px' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
            {copied && <span className="muted" style={{ color: 'lightgreen' }}>Copied!</span>}
            <button className="btn neutral" onClick={() => { setShowKey(false); setRevealedKey(''); }}>Hide</button>
          </div>
        </div>
      )}

      {err && <div style={{ color: 'crimson', marginTop: 8 }}>{err}</div>}
      {ok && <div style={{ color: 'lightgreen', marginTop: 8 }}>{ok}</div>}
      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <button className="btn" disabled={loading} onClick={async () => {
          setErr(''); setOk(''); setLoading(true);
          try {
            // Persist API key first if selected and revealed
            if (apiKeyId && revealedKey) {
              const rKey = await apiFetch(`/dev/${svc}/api-key`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ keyId: apiKeyId, key: revealedKey }) });
              if (!rKey.ok) {
                const j = await rKey.json().catch(() => ({}));
                throw new Error(j?.error?.message || `HTTP ${rKey.status}`);
              }
              setHasApiKey(true);
            }
            await onSaveConfig();
            setOk('Webhook configuration saved');
          } catch (e: any) { setErr(e?.message || String(e)); }
          finally { setLoading(false); }
        }}>Save Webhook</button>
      </div>
    </div>
  );
}
